/* 文章详情页 */
import React, {
  useEffect,
  useState,
  useCallback,
  createContext,
  useRef,
} from 'react'
import get from 'lodash/get'
import fetch from 'lib/decorated-fetch'
import dynamic from 'next/dynamic'

import getUrl from 'lib/url'
import { sendTeaCommEvent } from 'lib/tea-analyze'

import Head from 'Components/head'
import Statement from 'Components/statement'
import Author, { IAuthor } from 'Components/author'
import CarSystem, { ICarSystem } from 'Components/car-system'
import HotSpot from '../home/old-components/hot-spot'
import Loading from 'Components/loading'
import KoubeiInfo, { IKoubeiInfo } from 'Components/ugc/koubeiInfo'
import Interact from 'Components/ugc/interact'
import Comment from 'Components/comment'
import {
  DEFAULT_VIDEO_KEYWORDS,
  DEFAULT_VIDEO_DESCRIPTION,
  DEFAULT_ARTICLE_KEYWORDS,
  DEFAULT_ARTICLE_DESCRIPTION,
} from 'Constants/index'

import Content from './content'
import { ImageItem } from 'Components/ugc/imageList'
import useProcessSpy from 'Components/hooks/use-process-spy'
import useHandleBeforeLeave from 'Components/hooks/use-handle-before-leave'
import { getBoundingClientRect } from 'lib/utils'
import Recommend from 'Pages/article/recommend'
import Breadcrumb from 'Components/breadcrumb'
import { isNewVersion } from 'lib/version'
import ARTICLE_CONTENT_TYPE from 'Constants/articleType'
import redirectTo from 'lib/redirect-to'
import getUserInfo from 'lib/user-info'
import serializeObject from '@arsenal/arsenal/modules/serializeObject'

const Video = dynamic(() => import('Components/player'), {
  ssr: false,
  loading: () => <Loading />,
})

export interface IArticle {
  article_type: number
  group_id: number
  group_id_str: string
  group_source: number
  created_time: number
  image_urls: ImageItem[]
  vid: string | null
  create_time: number
  motor_title: string
  content: string
  read_count: number
  comment_count: number
  share_count: number
  digg_count: number
  user_digg: boolean
  content_rich_span: string
  log_pb: any
  share_info: any
  activity_info: any
  question_info: any
  motor_car_info: any
  motor_profile_info: any
  motor_cover_info: any
  motor_series_info: any
  motor_koubei_info: IKoubeiInfo
  motor_repost_info: any
  label_tag_list: {
    name: string
    image: ImageItem
    concern_id: number
  }[]
}

interface IProps {
  gid: string
  /**
   * 文章信息
   */
  article: IArticle
  /**
   * 作者信息
   */
  author: IAuthor
  /**
   * 相关车系
   */
  cars: ICarSystem[]
  is_motor: boolean
  articleType: number
  isKoubei: boolean // 是否口碑
}

export const ugcArticleContext = createContext<any>({
  updated_total_count: undefined,
  updateTotalCount: undefined,
})

function Article({
  gid,
  cars,
  article,
  isKoubei,
  is_motor,
  articleType,
}: IProps) {
  /* 每日热点数据 */
  const [hotSpot, setHotSpot] = useState([])
  const flagDom = useRef<HTMLDivElement | null>(null)
  const initialTop = useRef(0)
  const processNow = useRef(0)
  const commentRef = (node: HTMLDivElement) => {
    if (node === null) {
      return
    }
    initialTop.current = getBoundingClientRect(node).top
    flagDom.current = node
  }
  useHandleBeforeLeave(processNow, gid)
  useProcessSpy(flagDom, processNow, initialTop, () =>
    sendTeaCommEvent('article_read_over', {
      group_id: gid,
    })
  )

  useEffect(() => {
    obtainHotSpot()

    function preventDefault(e: any) {
      if (e.keyCode === 32 && e.target.nodeName === 'VIDEO') {
        e.preventDefault()
      }
    }
    window.addEventListener('keydown', preventDefault)
    return () => {
      window.removeEventListener('keydown', preventDefault)
    }
  }, [])
  const contentTypeObj = ARTICLE_CONTENT_TYPE[articleType] || {}
  useEffect(() => {
    sendTeaCommEvent('go_detail', {
      group_id: gid,
      content_type: contentTypeObj.value,
    })
  }, [contentTypeObj.value, gid])
  /* 获取每日热点数据 */
  function obtainHotSpot() {
    fetch(getUrl('/motor/m_api/pc_recommend_articles', 'motor.m_station.api'))
      .then((json) => {
        const result = json?.data?.filter(
          (e: { gid_str: string }) => e?.gid_str !== String(gid)
        )
        setHotSpot(result || [])
      })
      .catch((e) => {
        console.error('fetch hotspot error', e)
      })
  }

  const {
    article_type,
    motor_title,
    image_urls,
    vid,
    content,
    motor_profile_info,
    motor_koubei_info,
    group_id_str,
    created_time,
    comment_count,
    digg_count,
    user_digg,
    label_tag_list,
  } = article

  const [updated_total_count, setTotalCount] = useState(comment_count)
  const updateTotalCount = (count: number) => {
    setTotalCount(count)
  }

  const pageTitle = motor_title?.slice(0, 20)
  const description = (content ?? '')
    .replace(/\{!--.*?--\}/g, '')
    .replace(/<[^>]+>/g, '')
    .slice(0, 140)

  return (
    <div className="article clearfix">
      <Head
        title={
          is_motor && pageTitle
            ? `${pageTitle} - 懂车帝`
            : '懂车帝 - 说真的还得懂车帝'
        }
        keywords={
          pageTitle
            ? pageTitle
            : vid
              ? DEFAULT_VIDEO_KEYWORDS
              : DEFAULT_ARTICLE_KEYWORDS
        }
        description={
          description
            ? description
            : motor_title?.slice(0, 140) ||
              (vid ? DEFAULT_VIDEO_DESCRIPTION : DEFAULT_ARTICLE_DESCRIPTION)
        }
      />
      <Breadcrumb
        path={[{ name: '首页', href: '/home', as: '/' }, { name: '正文' }]}
        style={{ width: '750px' }}
      />
      <section className="content fl">
        {is_motor ? (
          <>
            <KoubeiInfo info={motor_koubei_info} />
            <Content
              image_urls={image_urls}
              content={content}
              title={motor_title}
            />
            {vid ? (
              <div className="video-wrapper">
                <Video vid={vid} group_id={group_id_str} />
              </div>
            ) : null}
            <ugcArticleContext.Provider
              value={{
                updated_total_count,
                updateTotalCount,
              }}
            >
              <>
                <div className="interact-wrapper">
                  <Interact
                    info={{
                      group_id: group_id_str,
                      ugcType: article_type,
                      behot_time: created_time,
                      comment_count: updated_total_count,
                      digg_count,
                      user_digg,
                      isVideo: !!vid,
                      label_tag_list,
                    }}
                    isKoubei={isKoubei}
                    articleType={articleType}
                  />
                </div>

                <div className="comment" ref={commentRef} id="comment">
                  <Comment
                    gid={group_id_str}
                    titleLeft={article_type === 3 ? '回答' : '评论'}
                    author_id={article.motor_profile_info?.user_id}
                  />
                </div>
              </>
            </ugcArticleContext.Provider>
          </>
        ) : (
          <>
            <div className="details">
              <article
                id="article"
                className="article-content"
                dangerouslySetInnerHTML={{
                  __html: `
                  <div style="width: 100%; text-align: center;">
                    <img src="${require('static/error-pic@3x.jpg')}" style="width: 240px; height: 240px; margin-bottom: 20px; display: block;"/>
                    文章消失了，再去看看别的吧～
                  </div>`,
                }}
              />
            </div>
            <Recommend gid={gid} contentType={contentTypeObj.value} />
          </>
        )}
      </section>
      <aside className="leaderboard fl">
        {motor_profile_info && <Author gid={gid} author={motor_profile_info} />}
        {cars.length ? <CarSystem gid={gid} cars={cars} /> : null}
        {hotSpot.length > 0 && <HotSpot hotSpot={hotSpot} />}
        <Statement />
      </aside>
      <style jsx>
        {`
          .article {
            width: 1190px;
            margin: 10px auto;
          }
          .content {
            width: 750px;
            margin-right: 50px;
            padding: 20px 0 0;
          }
          .video-wrapper {
            margin-top: 16px;
          }
          .interact-wrapper {
            margin-top: 16px;
          }
          .leaderboard {
            width: 390px;
          }
          .details {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px 0 60px;
          }
        `}
      </style>
    </div>
  )
}

Article.getInitialProps = async (ctx: any) => {
  const { query, req, asPath } = ctx
  const { gid, isKoubei } = query

  /* 获取文章类型 */
  const getArticleType = async () => {
    const userData = getUserInfo(req)
    const json = await fetch(
      getUrl(
        `/motor/pc/common/article/type?${serializeObject({
          group_id: query.gid,
          user_id: userData?.user_id,
        })}`
      )
    ).catch((err) => {
      // no-op
    })
    return json
  }

  const getArticle = async () => {
    const gidNum = Number(gid)
    if (Number.isNaN(gidNum) || gidNum <= 0) {
      return {}
    }
    const api = isKoubei
      ? '/motor/info/ugc/koubei/info/v1/'
      : '/motor/info/ugc/short/article/v1/'
    let result = {}
    try {
      const json = await fetch(
        getUrl(
          `${api}?group_id=${gid}&from=pc_station`,
          'motor.ugc.information'
        ),
        {
          headers: {
            Cookie: get(req, 'headers.cookie', ''),
          },
          req,
        }
      )
      result = json.data || {}
    } catch (err) {
      console.error(err)
    }
    return result
  }
  /* 获取相关车系数据 */
  const getCars = async () => {
    let result = []
    try {
      const json = await fetch(
        getUrl(
          `/motor/pc/content/get_information?group_id=${query.gid}`,
          'motor.pc_content.api'
        ),
        { req }
      )
      result = get(json, 'data.motor_material_car_info[0].info.list', [])
    } catch (err) {
      console.error(err)
    }
    return result
  }

  const [article, cars, articleType] = await Promise.all([
    getArticle(),
    getCars(),
    getArticleType(),
  ])

  if (articleType?.data?.type > 0 && articleType?.data?.type <= 4) {
    redirectTo(`/article/${gid}`, { res: ctx.res, status: 302 })
    return
  }
  if (!articleType?.data?.is_motor_article && articleType?.data?.is_visible) {
    redirectTo(`/404?from=${encodeURI(asPath)}`, { res: ctx.res, status: 302 })
    return
  }
  return {
    gid,
    article,
    cars,
    isKoubei,
    _pageType: isNewVersion(req?._isNewVersion) ? 'full auto-nav' : '',
    is_motor: articleType?.data?.is_motor_article,
    articleType: articleType?.data?.type,
  }
}

export default Article
